import { service } from '@ember/service'
import Component from '@glimmer/component'
import type { PhosphorIconName } from 'phosphor-icons'
import type SidebarService from 'teacher-ui-client/services/sidebar'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { tracked } from '@glimmer/tracking'
import { guidFor } from '@ember/object/internals'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import onLinkActive from 'teacher-ui-client/modifiers/on-link-active'
import { SideNavIconComponent } from 'teacher-ui-client/components/side-nav/icon'
import { NotificationDot } from 'teacher-ui-client/components/notification-dot'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { Collapser } from 'teacher-ui-client/components/collapser'
import { LinkTo } from '@ember/routing'
import { array, hash } from '@ember/helper'
import { not } from 'ember-truth-helpers'
import { fn } from '@ember/helper'
import { on } from '@ember/modifier'
import resetScroll from 'teacher-ui-client/modifiers/reset-scroll'
import type SideNavService from 'teacher-ui-client/services/side-nav'

interface SideNavAccordionListSignature {
  Element: HTMLLIElement
  Args: {
    iconUrl?: string
    iconAlt?: string
    phosphorIcon?: PhosphorIconName
    showNotificationDot?: boolean
    label: string
  }
  Blocks: {
    default: []
  }
}

export class SideNavAccordionList extends Component<SideNavAccordionListSignature> {
  @service sidebar!: SidebarService

  @service subscriptionType!: SubscriptionTypeService

  @service sideNav!: SideNavService

  @tracked animate = false

  get elementId() {
    return guidFor(this)
  }

  get isActivated() {
    return this.sidebar.activatedElementId === this.elementId
  }

  get isExpanded() {
    return this.sidebar.expandedItemId === this.elementId
  }

  get activeClassNames() {
    switch (this.subscriptionType.current) {
      case SubscriptionType.Reading:
        return {
          border: 'border-oceany-blue-350',
          button: 'text-oceany-blue-350',
        }
      case SubscriptionType.Maths:
        return {
          border: 'border-ms-green-350',
          button: 'text-ms-green-350',
        }
      case SubscriptionType.Writing:
        return {
          border: 'border-wl-blue-300',
          button: 'text-wl-blue-300',
        }
      default:
        return {
          border: '',
          button: '',
        }
    }
  }

  get isDisabled() {
    return this.sideNav.limitedAccess
  }

  <template>
    <li
      id={{this.elementId}}
      class="text-dusty-black-400 px-4 py-2 sm:block
        {{if this.isActivated this.activeClassNames.border}}
        {{if this.isDisabled 'pointer-events-none opacity-50'}}"
      {{onLinkActive this.sidebar.activate}}
      data-test-sidenav-accordion-item
      ...attributes
    >
      <button
        data-test-sidenav-accordion-label
        id="{{this.elementId}}-label"
        type="button"
        class="flex w-full cursor-pointer items-center gap-3 text-left select-none
          {{if this.isActivated this.activeClassNames.button 'text-dusty-black-400'}}"
        aria-expanded={{if this.isExpanded "true" "false"}}
        aria-controls="{{this.elementId}}-expand"
        aria-haspopup="true"
        {{on "click" (fn this.sidebar.toggleExpand this.elementId)}}
      >
        <SideNavIconComponent @url={{@iconUrl}} @alt={{@iconAlt}} @phosphorIcon={{@phosphorIcon}} />
        <div class="flex grow gap-x-1">
          <span class="align-middle text-sm font-medium">{{@label}}</span>
          {{#if @showNotificationDot}}
            <NotificationDot class="align-top" />
          {{/if}}
        </div>
        <PhosphorIcon
          class="h-4 w-4 transition-transform duration-200 ease-out {{if this.isExpanded 'rotate-90' ''}}"
          @icon="phosphor-caret-right-fill"
        />
      </button>
      <Collapser id="{{this.elementId}}-expand" @open={{this.isExpanded}} @static={{not this.animate}}>
        <ul
          role="menu"
          aria-labelledby="{{this.elementId}}-label"
          class="mt-[4px] overflow-hidden"
          {{resetScroll onClick=true}}
        >
          {{yield}}
        </ul>
      </Collapser>
    </li>
  </template>
}

interface SideNavAccordionItemSignature {
  Element: HTMLAnchorElement | HTMLButtonElement
  Args: {
    route?: string
    currentWhen?: string
    models?: unknown[]
    query?: Record<string, unknown>
    asButton?: boolean
  }
  Blocks: {
    default: []
  }
}

export class SideNavAccordionItem extends Component<SideNavAccordionItemSignature> {
  @service subscriptionType!: SubscriptionTypeService

  linkClassNames =
    'flex items-center text-black justify-between gap-2 py-[5px] pl-[39px] text-sm active-link:font-semibold'

  get themedClassNames() {
    switch (this.subscriptionType.current) {
      case SubscriptionType.Reading:
        return 'active-link:text-oceany-blue-350 hover:text-oceany-blue-350 focus:text-oceany-blue-350'
      case SubscriptionType.Maths:
        return 'active-link:text-ms-green-350 hover:text-ms-green-350 focus:text-ms-green-350'
      case SubscriptionType.Writing:
        return 'active-link:text-wl-blue-300 hover:text-wl-blue-300 focus:text-wl-blue-300'
      default:
        return ''
    }
  }

  <template>
    <li role="presentation">
      {{#if @route}}
        <LinkTo
          role="menuitem"
          class="{{this.linkClassNames}} {{this.themedClassNames}}"
          @route={{@route}}
          @models={{if @models @models (array)}}
          @query={{if @query @query (hash)}}
          @current-when={{@currentWhen}}
          ...attributes
        >
          {{yield}}
        </LinkTo>
      {{else if @asButton}}
        <button role="menuitem" type="button" class="{{this.linkClassNames}} {{this.themedClassNames}}" ...attributes>
          {{yield}}
        </button>
      {{else}}
        <a href="" role="menuitem" class="{{this.linkClassNames}} {{this.themedClassNames}}" ...attributes>
          {{yield}}
        </a>
      {{/if}}
    </li>
  </template>
}
